import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Note as NoteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  MoreHoriz as OtherIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { withOfflineFallback } from '../offline/offlineQuery';
import { readByClient } from '../offline/readers';
import { executeWrite, executeDelete } from '../offline/executeWrite';
import { useFormatInstant } from '../hooks/useClinicTimezone';

type NoteCategory = 'call' | 'email' | 'in_person' | 'other';

const CATEGORIES: { value: NoteCategory; label: string; icon: React.ReactElement; color: string }[] = [
  { value: 'call', label: 'Call', icon: <PhoneIcon fontSize="small" />, color: '#1976d2' },
  { value: 'email', label: 'Email', icon: <EmailIcon fontSize="small" />, color: '#7b1fa2' },
  { value: 'in_person', label: 'In-Person', icon: <PersonIcon fontSize="small" />, color: '#388e3c' },
  { value: 'other', label: 'Other', icon: <OtherIcon fontSize="small" />, color: '#616161' },
];

interface ClientNote {
  id: string;
  clientId: string;
  note: string;
  category?: NoteCategory | null;
  createdBy?: string;
  createdAt: string;
}

interface Props {
  clientId: string;
}

const getCategoryMeta = (cat?: NoteCategory | null) =>
  CATEGORIES.find(c => c.value === cat) || null;

const ClientNotesPanel: React.FC<Props> = ({ clientId }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  // ClientNote.createdAt is a mechanical INSTANT (`@default(now())`, DATE-KINDS
  // §1.4) — rendered in the CLINIC's zone, never the browser's.
  const formatInstant = useFormatInstant();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteCategory, setNoteCategory] = useState<NoteCategory | ''>('');
  const [filterCategory, setFilterCategory] = useState<NoteCategory | ''>('');

  const { data: notes = [], isLoading } = useQuery<ClientNote[]>(
    ['client-notes', clientId],
    withOfflineFallback(
      () => api.get('/client-notes', { params: { clientId } }).then(r => r.data),
      () => readByClient('clientNotes', clientId) as Promise<ClientNote[]>,
    ),
    { enabled: !!clientId },
  );

  const filteredNotes = useMemo(
    () => (filterCategory ? notes.filter(n => n.category === filterCategory) : notes),
    [notes, filterCategory],
  );

  const createMutation = useMutation(
    (payload: { note: string; category?: string }) =>
      executeWrite({
        entityType: 'clientNote',
        table: 'clientNotes',
        payload: { clientId, ...payload },
        networkFn: () => api.post('/client-notes', { clientId, ...payload }).then(r => r.data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-notes', clientId]);
        enqueueSnackbar('Note added', { variant: 'success' });
        setDialogOpen(false);
        setNoteText('');
        setNoteCategory('');
      },
      onError: () => { enqueueSnackbar('Failed to add note', { variant: 'error' }); },
    },
  );

  const deleteMutation = useMutation(
    (id: string) =>
      executeDelete({
        entityType: 'clientNote',
        table: 'clientNotes',
        entityId: id,
        networkFn: () => api.delete(`/client-notes/${id}`).then(r => r.data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-notes', clientId]);
        enqueueSnackbar('Note deleted', { variant: 'success' });
      },
      onError: () => { enqueueSnackbar('Failed to delete note', { variant: 'error' }); },
    },
  );

  const handleSave = () => {
    const payload: { note: string; category?: string } = { note: noteText };
    if (noteCategory) payload.category = noteCategory;
    createMutation.mutate(payload);
  };

  if (isLoading) {
    return <Typography color="textSecondary" variant="body2">Loading notes...</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <NoteIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={600}>
            Client Notes
          </Typography>
          <Typography variant="caption" color="textSecondary">
            ({filteredNotes.length}{filterCategory ? ` of ${notes.length}` : ''})
          </Typography>
        </Box>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Add Note
        </Button>
      </Box>

      {/* Filter chips */}
      <Box display="flex" gap={0.75} mb={1.5} flexWrap="wrap">
        <Chip
          label="All"
          size="small"
          variant={filterCategory === '' ? 'filled' : 'outlined'}
          color={filterCategory === '' ? 'primary' : 'default'}
          onClick={() => setFilterCategory('')}
        />
        {CATEGORIES.map(cat => (
          <Chip
            key={cat.value}
            icon={cat.icon}
            label={cat.label}
            size="small"
            variant={filterCategory === cat.value ? 'filled' : 'outlined'}
            color={filterCategory === cat.value ? 'primary' : 'default'}
            onClick={() => setFilterCategory(filterCategory === cat.value ? '' : cat.value)}
          />
        ))}
      </Box>

      {filteredNotes.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          {filterCategory ? 'No notes in this category.' : 'No notes yet.'}
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={1}>
          {filteredNotes.map(note => {
            const catMeta = getCategoryMeta(note.category);
            return (
              <Paper key={note.id} variant="outlined" sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Box flex={1}>
                    {catMeta && (
                      <Chip
                        icon={catMeta.icon}
                        label={catMeta.label}
                        size="small"
                        sx={{
                          mb: 0.5,
                          bgcolor: `${catMeta.color}14`,
                          color: catMeta.color,
                          fontWeight: 500,
                          '& .MuiChip-icon': { color: catMeta.color },
                        }}
                      />
                    )}
                    <Typography variant="body2">{note.note}</Typography>
                    <Typography variant="caption" color="textSecondary" data-testid="client-note-created-at">
                      {formatInstant(note.createdAt)}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => deleteMutation.mutate(note.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Client Note</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Category"
            value={noteCategory}
            onChange={e => setNoteCategory(e.target.value as NoteCategory | '')}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
          >
            <MenuItem value="">None</MenuItem>
            {CATEGORIES.map(cat => (
              <MenuItem key={cat.value} value={cat.value}>
                <Box display="flex" alignItems="center" gap={1}>
                  {cat.icon}
                  {cat.label}
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            autoFocus
            label="Note"
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            fullWidth
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setNoteText(''); setNoteCategory(''); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!noteText.trim() || createMutation.isLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientNotesPanel;
